import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';

import { Constants } from 'app/constants/constants';
import { Alias } from 'app/providers/bridge-api/alias.service';
import { Dashboard } from 'app/providers/bridge-api/dashboard.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { OriginTypes } from 'app/providers/semantria-api/semantria-account.service';
import { AnalysisGroup } from 'app/providers/storage-api/analysis-group-service';
import { Analysis } from 'app/providers/storage-api/analysis.service';
import { Collection } from 'app/providers/storage-api/collection.service';
import { ItemLabel } from 'app/providers/storage-api/object-label.service';
import { CalculationListItem } from 'app/providers/storage-api/predictive.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { EnableClassTelemetry } from '../../../services/otel.service';
import { ProjectCollection } from '../models/project-collection.model';

export interface ProjectItem {
  type: 'collection' | 'group' | 'dashboard' | 'analysis';
  name: string;
  id: string;
  modified: Date;
  created: Date;
  labels: Partial<ItemLabel>[];
  displayData: DisplayItem;
  collection?: ProjectCollection;
  group?: AnalysisGroup;
  dashboard?: Dashboard;
  analysis?: Analysis;
  calculation?: CalculationListItem;
  isSocial?: boolean;
}

export interface DisplayItem {
  docCount?: number;
  analysisCount?: number;
  isDriverAnalysis?: boolean;
  isAutoAnalysis?: boolean;
  autoAnalysisButtonData?: any;
  hasLLMFeature?: boolean;
  kda?: any;
  kdaLabels?: {
    breakdownColumn: string;
    dependentColumn: string;
    driverColumns: string[];
    breakdown: string[];
    weightField: string;
    widgetFilters: any;
    widgetGroupFilters: any;
    comparisonFilters: any;
    docCount: number;
  };
  columnsMapped?: number;
  isLocked?: boolean;
  activeUse?: ActiveUse[];
  hiddenDashboardCount?: number;
  aliases?: Alias[];
  configName?: string;
  collectionName?: string;
  smartSummaryName?: string;
  origin?: OriginTypes[];
  dashboardFilterCount?: number;
  analysisDataSources?: Analysis[];
  groupDataSources?: AnalysisGroup[];
  inUseDataSources?: DataSourcePillDisplay[];
  availableDataSources?: DataSourcePillDisplay[];
}

export interface ActiveUse {
  id: string;
  activeUseType: 'dashboard' | 'group';
  displayName: string;
  analysisID?: string;
}

export interface DataSourcePillDisplay {
  id: string;
  displayName: string;
  isSocial?: boolean;
  type: 'collection' | 'group';
  icon: string;
  color: string;
  tabId: 'collection' | 'group';
  itemId: string;
  projectionId: string;
  filteredOutActiveUse?: boolean;
}

export interface AnalysisEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  data: Analysis;
}

export interface DriverAnalysisEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  data: {
    projectId: string;
    collectionName: string;
    calculationId?: string;
    status: 'PENDING' | 'RUNNING' | 'COMPLETE' | 'ERROR' | 'DELETING' | 'DELETED';
  };
}

export interface AnalysisGroupEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  data: AnalysisGroup;
}

export interface AnalysisGroupCollectionEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  data: {
    collectionName: string;
    analysisName?: string;
  };
}

export interface CollectionEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  data: Collection;
}

const LAST_PROJECT_TAB_LOCALSTORAGE_KEY = 'lastProjectTabSelected';
const LAST_PROJECT_ITEM_LOCALSTORAGE_KEY = 'lastProjectItemSelected';
const LAST_PROJECT_SUBITEM_LOCALSTORAGE_KEY = 'subTableItemId';

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class ProjectDetailService {
  private localStorageService = inject(LocalStorageService);
  constructor(
    private configurationService: ConfigurationService,
    private groupService: GroupService
  ) {}

  public readonly projectItemIcons = {
    collection: {
      icon: Constants.COLLECTION_ICON,
      color: Constants.COLLECTION_ICON_COLOR,
      tooltip: 'collection',
    },
    group: {
      icon: Constants.GROUP_ICON,
      color: Constants.GROUP_ICON_COLOR,
      tooltip: 'group',
    },
    dynamicGroup: {
      icon: Constants.DYNAMIC_GROUP_ICON,
      color: Constants.GROUP_ICON_COLOR,
      tooltip: 'Dynamic Group',
    },
    dashboard: {
      icon: Constants.DASHBOARD_ICON,
      color: Constants.DASHBOARD_ICON_COLOR,
      tooltip: 'dashboard',
    },
    analysis: {
      icon: Constants.ANALYSIS_ICON,
      color: Constants.ANALYSIS_ICON_COLOR,
      tooltip: 'analysis',
    },
    socialCollection: {
      icon: Constants.SOCIAL_COLLECTION_ICON,
      color: Constants.SOCIAL_COLLECTION_ICON_COLOR,
      tooltip: 'socialCollection',
    },
  };

  private itemSource = new BehaviorSubject(null);
  currentItem = this.itemSource.asObservable();

  private tabSource = new Subject<{
    tabId: string;
    itemId?: string;
    projectionId?: string;
  }>();
  currentTab = this.tabSource.asObservable();

  private subTableItemSource = new Subject<ProjectItem>();
  currentSubTableItem = this.subTableItemSource.asObservable();

  private projectRefreshSource = new Subject<boolean>();
  projectRefresh = this.projectRefreshSource.asObservable();

  private collectionUploadSource = new Subject<Collection>();
  collectionUpload = this.collectionUploadSource.asObservable();

  private analysisEventSource = new Subject<AnalysisEvent>();
  analysisEvent = this.analysisEventSource.asObservable();

  private driverAnalysisEventSource = new Subject<DriverAnalysisEvent>();
  driverAnalysisEvent = this.driverAnalysisEventSource.asObservable();

  private analysisGroupEventSource = new Subject<AnalysisGroupEvent>();
  analysisGroupEvent = this.analysisGroupEventSource.asObservable();

  private analysisGroupCollectionEventSource = new Subject<AnalysisGroupCollectionEvent>();
  analysisGroupCollectionEvent = this.analysisGroupCollectionEventSource.asObservable();

  private collectionEventSource = new Subject<CollectionEvent>();
  collectionEvent = this.collectionEventSource.asObservable();

  private labelsExistenceChangedSource = new Subject<void>();
  labelsExistenceChanged$ = this.labelsExistenceChangedSource.asObservable();

  private analysisLabelsChangedSource = new Subject<{
    analysisName: string;
    labels: Partial<ItemLabel>[];
  }>();
  analysisLabelsChanged$ = this.analysisLabelsChangedSource.asObservable();

  private collectionLabelsChangedSource = new Subject<{
    collectionName: string;
    labels: Partial<ItemLabel>[];
  }>();
  collectionLabelsChanged$ = this.collectionLabelsChangedSource.asObservable();

  changeSelectedItem(item) {
    this.itemSource.next(item);
  }

  changeCurrentTab(tabId, itemId?, projectionId?) {
    this.tabSource.next({ tabId, itemId, projectionId });
  }

  changeCurrentSubTableItem(item: ProjectItem) {
    this.subTableItemSource.next(item);
  }

  projectRefreshNeeded() {
    this.projectRefreshSource.next(true);
  }

  goToCollectionUpload(collection?: Collection) {
    this.collectionUploadSource.next(collection);
  }

  onAnalysisEvent(type: 'CREATE' | 'UPDATE' | 'DELETE', data: Analysis) {
    this.analysisEventSource.next({ type, data });
  }

  onDriverAnalysisEvent(
    type: 'CREATE' | 'UPDATE' | 'DELETE',
    projectId: string,
    collectionName: string,
    calculationId: string,
    status: 'PENDING' | 'RUNNING' | 'COMPLETE' | 'ERROR' | 'DELETING' | 'DELETED'
  ) {
    this.driverAnalysisEventSource.next({
      type,
      data: { projectId, collectionName, calculationId, status },
    });
  }

  onAnalysisGroupEvent(type: 'CREATE' | 'UPDATE' | 'DELETE', data: AnalysisGroup) {
    this.analysisGroupEventSource.next({ type, data });
  }

  onAnalysisGroupCollectionEvent(
    type: 'CREATE' | 'UPDATE' | 'DELETE',
    collectionName: string,
    analysisName?: string
  ) {
    this.analysisGroupCollectionEventSource.next({
      type,
      data: { collectionName, analysisName },
    });
  }

  onCollectionEvent(type: 'CREATE' | 'UPDATE' | 'DELETE', data: Collection) {
    this.collectionEventSource.next({ type, data });
  }

  public async loadConfigurations(): Promise<{
    groupConfigurations: SemantriaConfiguration[];
    configurations: Map<string, SemantriaConfiguration>;
  }> {
    const groupConfigurations = await this.configurationService.getConfigurations(
      false,
      this.groupService.getSelectedGroupId()
    );
    const configurations = new Map<string, SemantriaConfiguration>();
    groupConfigurations.forEach((config) => {
      configurations.set(config.id, config);
    });
    return { groupConfigurations, configurations };
  }

  getLastSelectedTab() {
    return this.localStorageService.getItem(LAST_PROJECT_TAB_LOCALSTORAGE_KEY);
  }

  storeLastSelectedTab(tab) {
    this.localStorageService.setItem(LAST_PROJECT_TAB_LOCALSTORAGE_KEY, tab, 'local', true);
  }

  getLastSelectedItem() {
    return this.localStorageService.getItem(LAST_PROJECT_ITEM_LOCALSTORAGE_KEY);
  }

  storeLastSelectedItem(item) {
    this.localStorageService.setItem(LAST_PROJECT_ITEM_LOCALSTORAGE_KEY, item, 'local', true);
  }

  removeLastSelectedItem() {
    this.localStorageService.removeItem(LAST_PROJECT_ITEM_LOCALSTORAGE_KEY);
    this.removeLastSelectedSubItemId();
  }

  getLastSelectedSubItemId() {
    return this.localStorageService.getItem(LAST_PROJECT_SUBITEM_LOCALSTORAGE_KEY);
  }

  storeLastSelectedSubItemId(subItemId) {
    this.localStorageService.setItem(
      LAST_PROJECT_SUBITEM_LOCALSTORAGE_KEY,
      subItemId,
      'local',
      true
    );
  }

  removeLastSelectedSubItemId() {
    this.localStorageService.removeItem(LAST_PROJECT_SUBITEM_LOCALSTORAGE_KEY);
  }

  onLabelsExistenceChanged() {
    this.labelsExistenceChangedSource.next();
  }

  onAnalysisLabelsChanged(analysisName: string, labels: Partial<ItemLabel>[]) {
    this.analysisLabelsChangedSource.next({ analysisName, labels });
  }

  onCollectionLabelsChanged(collectionName: string, labels: Partial<ItemLabel>[]) {
    this.collectionLabelsChangedSource.next({ collectionName, labels });
  }
}
